import {
    KEYWORD_SECTION_MAP,
    type KeywordDefinition,
    buildKeywordIndex,
    normalizeKeywordTerm,
} from './keyword-section-map';

/**
 * Base 40K 11th-edition terms Horde Mode content assumes but `rules.json` never defines - that file only documents
 * Horde Mode's _additions and alterations_ to the Core Rules, not the Core Rules themselves, and there is no other
 * base-40K glossary/content file anywhere in the app.
 *
 * These are deliberately **not** verbatim rule reproductions (that would need ongoing sync with GW's own errata,
 * which is out of scope) - each `definition` is freshly authored, plain-language, app-original explanatory text
 * covering just enough to unblock a player mid-prompt. There is intentionally no `sectionId`: nothing in
 * `rules.json` defines these terms, so `KeywordRefComponent` renders an inline disclosure only, never a dead
 * link.
 */
export interface Base40kGlossaryTerm {
    readonly term: string;
    readonly aliases: readonly string[];
    readonly definition: string;
    /** Present when the term takes an `X` value (e.g. "Sustained Hits X"): the matcher links `term <value>`. */
    readonly params?: readonly string[];
}

export const BASE_40K_GLOSSARY: readonly Base40kGlossaryTerm[] = [
    {
        aliases: ['Battle-Shock', 'battle-shocked'],
        definition:
            "A unit that fails its Battle-shock test becomes Battle-shocked: it can't be affected by friendly Stratagems, contributes nothing to Objective Control, and can't perform actions until it passes a Battle-shock test in a later Command phase.",
        term: 'Battle-shock',
    },
    {
        aliases: ['ER'],
        definition:
            'The 1" bubble around every model in a unit - being within an enemy unit\'s Engagement Range restricts which moves and shooting attacks are legal.',
        term: 'Engagement Range',
    },
    {
        aliases: ['FNP'],
        definition:
            'An extra save rolled after normal saves fail: on a successful roll, damage that would otherwise be suffered is ignored instead.',
        params: [],
        term: 'Feel No Pain',
    },
    {
        aliases: ['invuln'],
        definition:
            "A saving throw unaffected by the attacking weapon's Armour Penetration, used in place of a normal armour save whenever it's the better option.",
        term: 'Invulnerable save',
    },
    {
        aliases: ['OC', 'Objective Control (OC)'],
        definition:
            "A per-model stat measuring how strongly a unit contests or controls an objective marker - a unit's total OC is compared against any enemies within range to decide who holds it.",
        term: 'Objective Control',
    },
    {
        aliases: [],
        definition:
            'A weapon ability: on a Critical Hit, that attack scores the listed number of additional automatic hits.',
        params: [],
        term: 'Sustained Hits',
    },
    {
        aliases: [],
        definition:
            'A weapon ability: a Critical Hit with that weapon automatically counts as a successful wound roll, skipping the wound-roll step entirely.',
        term: 'Lethal Hits',
    },
    {
        aliases: [],
        definition:
            "A weapon ability: a Critical Wound with that weapon inflicts its damage as Mortal Wounds instead, bypassing the target's saves entirely.",
        term: 'Devastating Wounds',
    },
    {
        aliases: [],
        definition: 'A weapon ability letting the bearer re-roll wound rolls made for that weapon.',
        term: 'Twin-linked',
    },
    {
        aliases: [],
        definition:
            'A weapon ability granting +1 to wound rolls made with it when the bearer made a Charge move this turn.',
        term: 'Lance',
    },
    {
        aliases: ['ASSAULT'],
        definition:
            'A weapon ability that lets it be fired even after the bearer has made an Advance move this turn.',
        term: '[ASSAULT]',
    },
    {
        aliases: [],
        definition:
            "A weapon ability marking it as a close-range sidearm rather than a primary weapon - Horde Mode uses it to identify a unit's 'side-arms', which don't count toward its main loadout when deciding movement priority.",
        term: '[CLOSE-QUARTERS]',
    },
    {
        aliases: [],
        definition:
            'Damage that bypasses saving throws and Feel No Pain, removing wounds directly from the target model.',
        term: 'Mortal Wounds',
    },
    {
        aliases: [],
        definition:
            'An ability making a unit harder to hit at range: ranged attacks against it suffer a -1 to their hit roll unless the attacker is within half range.',
        term: 'Stealth',
    },
];

/**
 * `term`/alias → {@link Base40kGlossaryTerm} lookup for the ~40K-core terms Horde content assumes.
 */
export const BASE_40K_TERM_MAP: ReadonlyMap<string, Base40kGlossaryTerm> = buildKeywordIndex(BASE_40K_GLOSSARY);

/**
 * Resolves any keyword/alias (Horde-specific or base-40K) to a {@link KeywordDefinition}, or `null` if it isn't in
 * either glossary. Horde-glossary entries (which always have a `rules.json` section) win over base-40K entries on
 * a name collision, since they're the more specific, more directly-relevant definition for this app. This is the
 * single source of truth `KeywordRefComponent` (`libs/shared/ui`, `type:ui`) calls directly - it lives here, in
 * `type:util`, rather than in `shared-data-access-content` (`type:data-access`), because the Nx module-boundary
 * rules only let `type:ui` depend on `type:ui`/`type:util`. It's also re-exported from
 * `shared-data-access-content`'s `rule-lookup.ts` for feature code that already imports from there.
 */
export function resolveKeywordDefinition(term: string): KeywordDefinition | null {
    const key = normalizeKeywordTerm(term);
    const hordeEntry = KEYWORD_SECTION_MAP.get(key);

    if (hordeEntry) {
        return { definition: hordeEntry.definition, sectionId: hordeEntry.sectionId };
    }

    const baseEntry = BASE_40K_TERM_MAP.get(key);

    return baseEntry ? { definition: baseEntry.definition } : null;
}
